import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Affix,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2 } from 'react-icons/bs';
import { useHistory } from 'react-router-dom';

import PPPageHeader from '../../../components/pageHeader/PPPageHeader';
import ModalSelectProductView from '../../../components/modalSelectProduct';
import ProductImage from '../../../components/productImage';
import { isHandledByInterceptor } from '../../../utils/apiError';
import { formatPrice } from '../../../utils/format';
import '../Order.less';

dayjs.extend(customParseFormat);

const { Text, Title } = Typography;

const LIST_PATH = '/merchant/inventory/control';

/** Тооллогын хүсэлт үүсгэх эрхтэй гэрээний төрлүүд */
const ALLOWED_CONTRACT_TYPES = ['WO', 'W'];

/**
 * Формын талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдаж,
 * баруун талд зай үлдэнэ.
 */
const FORM_MAX_WIDTH = 640;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 320;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Картын гарчиг: зүүн талд дүрс */
const CardTitle = ({ icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    {icon}
    {children}
  </span>
);

/** Барааны зураг + нэр + багц код */
const ProductCell = ({ image, title, code }) => (
  <Space size={12} align="center">
    <ProductImage
      src={image}
      alt={title ?? 'Барааны зураг'}
      size={IMAGE_SIZE}
      style={{ objectFit: 'cover', borderRadius: 4, boxShadow: IMAGE_SHADOW }}
    />
    <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
      <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: title }}>
        {val(title)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, maxWidth: TITLE_MAX_WIDTH }} ellipsis>
        {code}
      </Text>
    </Space>
  </Space>
);

const getColumns = (removeProduct) => [
  {
    key: 'index',
    title: '№',
    width: 48,
    align: 'center',
    render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
  },
  {
    key: 'product',
    title: 'Бараа',
    render: (_, record) => (
      <ProductCell
        image={record?.productImage}
        title={record?.productTitle}
        code={record?.batchCode}
      />
    ),
  },
  {
    key: 'unitPrice',
    title: 'Нэгжийн үнэ',
    align: 'center',
    width: 160,
    render: (_, record) => (record?.unitPrice != null ? formatPrice(record.unitPrice) : '-'),
  },
  {
    key: 'stock',
    title: 'Үлдэгдэл',
    align: 'center',
    width: 140,
    render: (_, record) => (record?.stock != null ? `${record.stock} ш` : '-'),
  },
  {
    key: 'action',
    title: '',
    width: 60,
    align: 'center',
    fixed: 'right',
    render: (_, record) => (
      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeProduct(record)} />
    ),
  },
];

const AddInventoryControl = () => {
  const history = useHistory();
  const [form] = Form.useForm();

  const [visibleProduct, setVisibleProduct] = useState(false);
  const [contractTypes, setContractTypes] = useState(null);
  const [warehouseCode, setWarehouseCode] = useState('');
  const [products, setProducts] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState({ brand: '', category: '' });
  const [warehouseList, setWarehouseList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [finalSelectedProducts, setFinalSelectedProducts] = useState([]);
  const [inventoryDate, setInventoryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  const routes = [
    { to: { pathname: LIST_PATH }, breadcrumbName: 'Тооллого' },
    { path: 'first', breadcrumbName: 'Тооллогын хүсэлт' },
  ];

  const fetchWarehouses = async () => {
    setWarehousesLoading(true);
    try {
      const { data } = await axios.get('/api/v1.0/request/invWarehouse');
      if (data.statusCode === 200) {
        setWarehouseList(data?.result ?? []);
      }
    } catch (error) {
      console.error('invWarehouse: ', error);
    } finally {
      setWarehousesLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
    axios
      .get('/api/v1/request/delivery-reference')
      .then(({ data }) => {
        if (data.statusCode === 200) {
          const types = data?.result?.contract?.data?.contractType;
          setContractTypes(Array.isArray(types) ? types : []);
        }
      })
      .catch((error) => {
        console.error('delivery-reference: ', error);
        setContractTypes([]);
      });
  }, []);

  const fetchProducts = async (whCode = '', searchValue = '', filterValues = filter) => {
    setProductsLoading(true);
    try {
      const { data } = await axios.post('/api/v1.0/request/getInvProductsByWH', {
        warehouseCode: whCode,
        searchValue: searchValue ?? '',
        //Брэнд болон Категори filter
        brand: filterValues?.brand ?? '',
        category: filterValues?.category ?? '',
      });
      if (data?.statusCode === 200) {
        data.result.map((item) =>
          finalSelectedProducts.forEach((product) => {
            if (product._id === item._id) {
              item.checked = true;
            }
          })
        );
        setProducts(data.result);
      }
    } catch (error) {
      console.error('getInvProductsByWH: ', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const createInventory = async () => {
    if (!inventoryDate) {
      message.error('Тооллого хийх огноогоо оруулна уу.');
      return;
    }
    if (!warehouseCode) {
      message.error('Бараа татах агуулахаа сонгоно уу.');
      return;
    }
    if (finalSelectedProducts.length === 0) {
      message.error('Тооллого хийх бараагаа сонгоно уу.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post('/api/v1.0/request/invCreateRequest', {
        selectedProducts: finalSelectedProducts.map((item) => item._id),
        inventoryDate,
        warehouseCode,
      });

      if (data?.statusCode === 200) {
        message.success('Тооллогын хүсэлт амжилттай үүслээ.');
        history.push(LIST_PATH);
        return;
      }

      // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
      if (!isHandledByInterceptor(data)) {
        message.error(data?.message ?? 'Тооллогын хүсэлт үүсгэхэд алдаа гарлаа.');
      }
    } catch (error) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('invCreateRequest: ', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDateChange = (_date, dateString) => {
    setInventoryDate(dateString);
  };

  const onChangeWarehouse = (whCode) => {
    setWarehouseCode(whCode);
    setSelectedProducts([]);
    setFinalSelectedProducts([]);
    fetchProducts(whCode, searchText, filter);
  };

  const closeModal = () => setVisibleProduct(false);

  const onClickSelectProduct = () => {
    if (warehouseCode) {
      setVisibleProduct(true);
    } else {
      message.error('Бараа татах агуулахаа сонгоно уу.');
    }
  };

  const onSearchProduct = (value, event, page = 1, options = {}) => {
    const searchValue = value || '';
    const { brand = filter.brand, category = filter.category } = options;
    setSearchText(searchValue);
    fetchProducts(warehouseCode, searchValue, { brand, category });
  };

  const onFilterChange = (key, value) => {
    const nextFilter = { ...filter, [key]: value || '' };
    setFilter(nextFilter);
    onSearchProduct(searchText, null, 1, nextFilter);
  };

  const handleCheckProduct = (i, checked) => {
    setSelectedProducts([products[i], ...selectedProducts]);
    products[i].checked = checked;
  };

  /** Сонгосон жагсаалтаас нэг барааг хасна (модалын checkbox-ыг нь ч цуцална) */
  const removeProduct = (record) => {
    setFinalSelectedProducts((prev) => prev.filter((item) => item._id !== record._id));
    setSelectedProducts((prev) => prev.filter((item) => item._id !== record._id));
    const found = products.find((item) => item._id === record._id);
    if (found) found.checked = false;
  };

  const handleOk = () => {
    let selProdClone = [...selectedProducts];
    const listAdd = [];
    products.forEach((item) => {
      const isItemSelected = selectedProducts.find(
        (el) => el?._id === item?.id && el?.expiryDate === item?.expiryDate
      );

      if (searchText && !isItemSelected) {
        listAdd.push(...finalSelectedProducts, item);
        item.checked = true;
      }
      if (item.checked && !isItemSelected && !searchText) {
        listAdd.push(item);
      }

      if (!item.checked && isItemSelected) {
        selProdClone = selProdClone.filter(
          (el) =>
            el._id !== item._id || (el._id === item._id && el?.expiryDate !== item?.expiryDate)
        );
      }
    });
    setFinalSelectedProducts([...listAdd]);
    setSearchText('');
    closeModal();
  };

  /** Өнөөдрөөс өмнөх огноог сонгуулахгүй */
  const disabledDate = (current) => current && current < dayjs().startOf('day');

  const goBack = () => history.push(LIST_PATH);

  // Зөвхөн агуулахын гэрээтэй (WO, W) хэрэглэгч тооллогын хүсэлт үүсгэнэ
  const canCreate = (contractTypes ?? []).some((type) => ALLOWED_CONTRACT_TYPES.includes(type));

  return (
    <div>
      <PPPageHeader title="Тооллогын хүсэлт үүсгэх" breadcrumb={{ routes }} onBack={goBack} />

      {visibleProduct && (
        <ModalSelectProductView
          isModalVisible={visibleProduct}
          handleOk={handleOk}
          handleCancel={closeModal}
          productList={products}
          handleCheckProduct={handleCheckProduct}
          onSearchProduct={onSearchProduct}
          searchText={searchText}
          loading={productsLoading}
          showFilter
          filterValues={filter}
          onFilterChange={onFilterChange}
        />
      )}

      <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
        {/* contractTypes === null үед хүсэлт нь дуусаагүй тул шийдвэр гаргахгүй */}
        {contractTypes !== null && !canCreate ? (
          <Card size="middle">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" size={4}>
                  <Text strong>Тооллогын хүсэлт үүсгэх боломжгүй</Text>
                  <Text type="secondary">
                    Тооллого нь агуулахын гэрээтэй харилцагчид зориулагдсан.
                  </Text>
                </Space>
              }
              style={{ padding: '32px 0', margin: 0 }}
            />
          </Card>
        ) : (
          <>
            <Card
              size="middle"
              title={
                <CardTitle icon={<AiOutlineInfoCircle />}>
                  <Title level={5} style={{ margin: 0 }}>
                    Тооллогын мэдээлэл
                  </Title>
                </CardTitle>
              }
            >
              <Form layout="vertical" form={form} requiredMark>
                <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Тооллого хийх огноо" required style={{ marginBottom: 0 }}>
                      <DatePicker
                        disabledDate={disabledDate}
                        onChange={handleDateChange}
                        placeholder="Огноо сонгоно уу"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Агуулах" required style={{ marginBottom: 0 }}>
                      <Select
                        onChange={onChangeWarehouse}
                        placeholder="Агуулах сонгоно уу"
                        value={warehouseCode || undefined}
                        loading={warehousesLoading}
                        options={warehouseList.map((warehouse) => ({
                          value: warehouse.code,
                          label: warehouse?.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>

            <Card
              size="middle"
              title={
                <CardTitle icon={<BsBox2 />}>
                  Тооллого хийх бараа
                  {finalSelectedProducts.length > 0 && (
                    <Text type="secondary">({finalSelectedProducts.length})</Text>
                  )}
                </CardTitle>
              }
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={onClickSelectProduct}>
                  Бараа нэмэх
                </Button>
              }
              styles={{ body: { padding: finalSelectedProducts.length ? 0 : undefined } }}
            >
              <div style={{ padding: '10px' }}>
                {finalSelectedProducts.length ? (
                  <Table
                    columns={getColumns(removeProduct)}
                    dataSource={finalSelectedProducts}
                    rowKey={(record, index) => record?._id ?? index}
                    size="middle"
                    pagination={false}
                    bordered
                    scroll={{ x: 'max-content' }}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      warehouseCode ? 'Бараа сонгогдоогүй байна.' : 'Эхлээд агуулахаа сонгоно уу.'
                    }
                    style={{ margin: 0 }}
                  />
                )}
              </div>
            </Card>

            {/* Дэлгэцийн доод хэсэгт наалдана — урт жагсаалт гүйлгэхэд ч харагдана */}
            <Affix offsetBottom={16}>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'center',
                  padding: 12,
                  backgroundColor: '#fff',
                  border: '1px solid #f1f2f4',
                  borderRadius: 8,
                  boxShadow: '0 1px 2px rgba(16, 22, 26, 0.04)',
                }}
              >
                <Button onClick={goBack}>Буцах</Button>
                <Button type="primary" loading={submitting} onClick={createInventory}>
                  Хүсэлт үүсгэх
                </Button>
              </div>
            </Affix>
          </>
        )}
      </Space>
    </div>
  );
};

export default AddInventoryControl;
